import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Badge,
  cn,
} from '@design-system';

import { Check, Loader2, Pencil, Send } from 'lucide-react';
import { useState } from 'react';

import { useSendReminderStore } from '../store/store';

export function SendReminderModal() {
  const {
    reminderConfig,
    isOpen,
    isLoading,
    isEmailValid,
    canSend,
    isEmailBodyLocked,
    handleConfigChange,
    handleSendReminder,
    handleClose,
  } = useSendReminderStore();

  const [isEditing, setIsEditing] = useState(false);

  const { email, firstName, lastName, emailBody } = reminderConfig;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] w-full overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Send Intake Form Reminder</DialogTitle>
          <DialogDescription>
            Send a reminder to your client to complete their intake form
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <fieldset disabled={isLoading}>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Jane"
                    value={firstName}
                    onChange={({ target }) => handleConfigChange('firstName', target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={({ target }) => handleConfigChange('lastName', target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="client@example.com"
                  value={email}
                  onChange={({ target }) => handleConfigChange('email', target.value)}
                  className={cn({
                    'border-red-500': email.length > 0 && !isEmailValid,
                  })}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="cursor-default">Email template</Label>
                  {!isEmailBodyLocked && (
                    <Badge
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => !isLoading && setIsEditing(!isEditing)}
                    >
                      {isEditing ? (
                        <>
                          <Check className="size-3" />
                          <span className="pr-1">Save</span>
                        </>
                      ) : (
                        <>
                          <Pencil className="size-3" />
                          <span className="pr-1">Edit</span>
                        </>
                      )}
                    </Badge>
                  )}
                </div>
                {isEmailBodyLocked && (
                  <p className="text-muted-foreground text-xs">
                    Your pilot program sets this wording, so it can't be edited.
                  </p>
                )}
                {isEditing && !isEmailBodyLocked ? (
                  <Textarea
                    value={emailBody}
                    onChange={({ target }) => handleConfigChange('emailBody', target.value)}
                    rows={6}
                    className="text-sm"
                    placeholder="Enter your reminder message here..."
                  />
                ) : (
                  <div className="bg-muted rounded-lg p-3 min-h-[100px]">
                    {emailBody ? (
                      <p className="text-foreground text-sm whitespace-pre-wrap">"{emailBody}"</p>
                    ) : (
                      <p className="text-muted-foreground text-sm italic">
                        No email template configured. Click "Edit" to add a message.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          <Button
            className="w-full"
            onClick={handleSendReminder}
            disabled={!canSend || isLoading}
          >
            {isLoading ? <Loader2 className="animate-spin" /> : <Send />}
            {isLoading ? 'Sending...' : 'Send Reminder'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
